/**
 * The Google Maps JavaScript API (Places), loaded on demand - once per page.
 *
 * It used to be injected as soon as the address box mounted, which put
 * ~380 KB of Maps on the booking portal's first step for every visitor
 * (QA 2026-09-25 #14), including the ones the marketing site hands over
 * with an address already picked, who never touch the box at all. Now it is
 * requested only when something needs it:
 *
 *   - the address box is focused, tapped or hovered (SearchLocationInput),
 *   - the hand-off has to resolve free text (util/placesLookup), or
 *   - a numbered pick has to be looked up again (util/placesLookup).
 *
 * Same URL, same key and the same single-script rule as the widget that
 * used to load it (react-google-autocomplete): a Maps script that is already
 * on the page - another component's, e.g. the fleet map - is reused, never
 * loaded twice.
 */

export const MAPS_SCRIPT_BASE = 'https://maps.googleapis.com/maps/api/js';
const MAPS_CALLBACK = '__aprPlacesReady';

type PlacesLib = typeof google.maps.places;

export function placesLib(): PlacesLib | null {
  const maps = (window as unknown as { google?: typeof google }).google?.maps;
  return maps && maps.places ? maps.places : null;
}

let pending: Promise<PlacesLib | null> | null = null;
const listeners: Array<() => void> = [];

function settle(): PlacesLib | null {
  const lib = placesLib();
  if (lib) {
    listeners.splice(0).forEach((fn) => {
      try {
        fn();
      } catch (e) {
        // a listener's problem is its own
      }
    });
  }
  return lib;
}

/**
 * Calls `fn` once the Places library is on the page: right away when it
 * already is, otherwise when a load this module started finishes. Returns
 * an unsubscribe.
 */
export function onPlacesLoaded(fn: () => void): () => void {
  if (placesLib()) {
    fn();
    return () => undefined;
  }
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i !== -1) listeners.splice(i, 1);
  };
}

/**
 * Loads the Maps script with the Places library, once. Resolves with the
 * library, or `null` when it cannot be had (no API key configured, script
 * blocked or offline). Never rejects.
 *
 * `language` only counts on the first load of the page, as with any Maps
 * script: a script already loading or loaded is reused as it is.
 */
export function loadPlaces(
  language?: string | null,
): Promise<PlacesLib | null> {
  const ready = placesLib();
  if (ready) return Promise.resolve(ready);
  if (pending) return pending;

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src*="${MAPS_SCRIPT_BASE}"]`,
  );
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!existing && !apiKey) {
    // Nothing to load it with. Whoever needs the library waits for it the
    // way it always has (placesLookup polls), and gives up on its own.
    return Promise.resolve(null);
  }

  pending = new Promise((resolve) => {
    const done = () => resolve(settle());
    if (existing) {
      existing.addEventListener('load', done);
      existing.addEventListener('error', done);
      return;
    }
    // `loading=async` + a callback is Google's current way to load the API
    // (the direct load logged a "loaded directly without loading=async"
    // warning, QA 2026-09-25 #52); the callback runs once the Places
    // library is ready.
    const w = window as unknown as Record<string, unknown>;
    w[MAPS_CALLBACK] = () => {
      delete w[MAPS_CALLBACK];
      done();
    };
    const script = document.createElement('script');
    script.src = `${MAPS_SCRIPT_BASE}?libraries=places&key=${apiKey}${
      language ? `&language=${encodeURIComponent(language)}` : ''
    }&loading=async&callback=${MAPS_CALLBACK}`;
    script.async = true;
    script.addEventListener('error', () => {
      // A later call may try again (e.g. the visitor comes back online).
      pending = null;
      resolve(null);
    });
    document.body.appendChild(script);
  });
  return pending;
}

/** Tests only: forget a load started by an earlier test. */
export function resetMapsLoaderForTests(): void {
  pending = null;
  listeners.splice(0);
}
